import React from 'react';
import { honours } from '../data/honours';
import { fundamentalBeliefs } from '../data/beliefs';
import { bibleTruthsSections } from '../data/bibleTruths';

export default function LandingPage({ onSignIn, onSignUp, onGoogleSignIn }) {
    // Calculate stats
    const honoursCount = honours.length;
    const beliefsCount = fundamentalBeliefs.length;

    // Calculate total Bible study lessons/topics
    // Flatten lessons from all sections
    const bibleStudiesCount = bibleTruthsSections.reduce((acc, section) => {
        return acc + (section.lessons ? section.lessons.length : 0);
    }, 0);

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
            {/* Navigation Header */}
            <nav className="bg-white/95 backdrop-blur-sm shadow-lg border-b border-indigo-100">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-20">
                        <div className="flex items-center space-x-3">
                            <div className="w-12 h-12 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-full flex items-center justify-center">
                                <span className="text-white font-bold text-xl">MG</span>
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                                    Master Guide
                                </h1>
                                <p className="text-sm text-gray-500">Pathfinder Leadership Platform</p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={onSignIn}
                                className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white px-8 py-3 rounded-xl font-semibold transition duration-300 transform hover:scale-105 shadow-lg"
                            >
                                Sign In
                            </button>
                            <button
                                onClick={onSignUp}
                                className="border-2 border-indigo-200 text-indigo-600 hover:bg-indigo-50 px-8 py-3 rounded-xl font-semibold transition duration-300"
                            >
                                Get Started
                            </button>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <div className="relative overflow-hidden">
                {/* Background decoration */}
                <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/5 via-purple-600/5 to-blue-600/5"></div>
                <div className="absolute top-20 right-20 w-72 h-72 bg-gradient-to-br from-indigo-300/20 to-purple-300/20 rounded-full blur-3xl"></div>
                <div className="absolute bottom-20 left-20 w-96 h-96 bg-gradient-to-br from-blue-300/20 to-indigo-300/20 rounded-full blur-3xl"></div>

                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
                    <div className="text-center">
                        {/* Welcome Message */}
                        <div className="inline-flex items-center px-6 py-3 rounded-full bg-indigo-100 text-indigo-800 font-medium mb-8">
                            <span className="mr-2">👋</span>
                            Welcome to the Future of Master Guide Training
                        </div>

                        <h1 className="text-5xl md:text-7xl font-bold text-gray-900 mb-8 leading-tight">
                            Pathfinder
                            <span className="block bg-gradient-to-r from-indigo-600 via-purple-600 to-blue-600 bg-clip-text text-transparent">
                                Master Guide
                            </span>
                        </h1>
                        <p className="text-xl md:text-2xl text-gray-600 mb-12 max-w-4xl mx-auto leading-relaxed">
                            Empowering youth leaders through spiritual growth, practical skills, and Christ-centered mentorship in the Seventh-day Adventist tradition.
                        </p>

                        <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-16">
                            <button
                                onClick={onSignUp}
                                className="group bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold py-4 px-10 rounded-2xl text-lg transition duration-300 transform hover:scale-105 shadow-xl hover:shadow-2xl"
                            >
                                <span className="flex items-center">
                                    Start Your Journey
                                    <svg className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                </span>
                            </button>
                            <button
                                onClick={onGoogleSignIn}
                                className="group bg-white hover:bg-gray-50 text-gray-700 font-semibold py-4 px-10 rounded-2xl text-lg border-2 border-gray-200 hover:border-gray-300 transition duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center gap-3"
                            >
                                <svg className="w-6 h-6" viewBox="0 0 24 24">
                                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                                </svg>
                                Continue with Google
                            </button>
                            <button
                                onClick={() => {
                                    const aboutSection = document.getElementById('about');
                                    if (aboutSection) {
                                        aboutSection.scrollIntoView({ behavior: 'smooth' });
                                    }
                                }}
                                className="bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white font-semibold py-4 px-10 rounded-2xl text-lg border-2 border-white/20 hover:border-white/30 transition duration-300"
                            >
                                Learn More
                            </button>
                        </div>

                        {/* Quick stats - DYNAMIC */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
                            <div className="text-center">
                                <div className="text-3xl font-bold text-indigo-600 mb-2">{bibleStudiesCount}</div>
                                <div className="text-gray-600">Bible Study Topics</div>
                            </div>
                            <div className="text-center">
                                <div className="text-3xl font-bold text-purple-600 mb-2">{beliefsCount}</div>
                                <div className="text-gray-600">Core Beliefs Covered</div>
                            </div>
                            <div className="text-center">
                                <div className="text-3xl font-bold text-blue-600 mb-2">{honoursCount}+</div>
                                <div className="text-gray-600">Skills & Honours</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* About Master Guiding Section */}
            <div id="about" className="py-24 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-20">
                        <div className="inline-flex items-center px-4 py-2 rounded-full bg-indigo-100 text-indigo-800 font-medium mb-6">
                            <span className="mr-2">⚡</span>
                            Transform Your Leadership
                        </div>
                        <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-8">
                            What is Master Guide?
                        </h2>
                        <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
                            The Master Guide program is the highest level of Pathfinder leadership training, designed to develop spiritually mature leaders who can guide and mentor youth in their spiritual journey.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
                        <div className="group hover:scale-105 transition duration-300">
                            <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-2xl p-8 text-center h-full border border-indigo-100 hover:border-indigo-200 hover:shadow-xl transition duration-300">
                                <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition duration-300">
                                    <span className="text-3xl">⛪</span>
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-4">Spiritual Leadership</h3>
                                <p className="text-gray-600 leading-relaxed">
                                    Develop deep spiritual maturity and the ability to lead worship, conduct Bible studies, and mentor youth in their faith journey.
                                </p>
                            </div>
                        </div>

                        <div className="group hover:scale-105 transition duration-300">
                            <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-2xl p-8 text-center h-full border border-emerald-100 hover:border-emerald-200 hover:shadow-xl transition duration-300">
                                <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-green-500 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition duration-300">
                                    <span className="text-3xl">🎯</span>
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-4">Practical Skills</h3>
                                <p className="text-gray-600 leading-relaxed">
                                    Master outdoor skills, emergency preparedness, health and fitness, and other practical abilities essential for youth ministry.
                                </p>
                            </div>
                        </div>

                        <div className="group hover:scale-105 transition duration-300">
                            <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-2xl p-8 text-center h-full border border-purple-100 hover:border-purple-200 hover:shadow-xl transition duration-300">
                                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-violet-500 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition duration-300">
                                    <span className="text-3xl">👥</span>
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-4">Youth Mentorship</h3>
                                <p className="text-gray-600 leading-relaxed">
                                    Learn to effectively guide, counsel, and inspire young people while building strong, Christ-centered relationships.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-blue-600 rounded-3xl p-12 text-white text-center shadow-2xl">
                        <h3 className="text-3xl md:text-4xl font-bold mb-6">Master Guide Pledge</h3>
                        <p className="text-xl md:text-2xl italic mb-8 leading-relaxed max-w-4xl mx-auto">
                            "By the grace of God, I will be pure, kind, and true. I will keep the Pathfinder Law. I will be a servant of God and a friend to man."
                        </p>
                        <div className="text-sm opacity-90 bg-white/10 rounded-xl px-6 py-3 inline-block">
                            This pledge represents our commitment to Christian character and service to others.
                        </div>
                    </div>
                </div>
            </div>

            {/* Requirements Section */}
            <div className="py-20 bg-gray-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                            Master Guide Requirements
                        </h2>
                        <p className="text-xl text-gray-600">
                            The path to becoming a Master Guide involves spiritual, physical, and practical development.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-12">
                        <div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-6">Spiritual Development</h3>
                            <div className="space-y-4">
                                <div className="flex items-start">
                                    <div className="bg-blue-100 rounded-full p-2 mr-4 mt-1">
                                        <span className="text-blue-600 font-bold text-sm">✓</span>
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-gray-900">Bible Study Mastery</h4>
                                        <p className="text-gray-600">Complete comprehensive Bible study program and memorize key scriptures</p>
                                    </div>
                                </div>
                                <div className="flex items-start">
                                    <div className="bg-blue-100 rounded-full p-2 mr-4 mt-1">
                                        <span className="text-blue-600 font-bold text-sm">✓</span>
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-gray-900">28 Fundamental Beliefs</h4>
                                        <p className="text-gray-600">Study and understand Seventh-day Adventist doctrinal foundations</p>
                                    </div>
                                </div>
                                <div className="flex items-start">
                                    <div className="bg-blue-100 rounded-full p-2 mr-4 mt-1">
                                        <span className="text-blue-600 font-bold text-sm">✓</span>
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-gray-900">Personal Devotions</h4>
                                        <p className="text-gray-600">Maintain consistent daily devotional practice and spiritual journal</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-6">Leadership & Skills</h3>
                            <div className="space-y-4">
                                <div className="flex items-start">
                                    <div className="bg-green-100 rounded-full p-2 mr-4 mt-1">
                                        <span className="text-green-600 font-bold text-sm">✓</span>
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-gray-900">Honours Program</h4>
                                        <p className="text-gray-600">Complete specialized honours in various skill areas and interests</p>
                                    </div>
                                </div>
                                <div className="flex items-start">
                                    <div className="bg-green-100 rounded-full p-2 mr-4 mt-1">
                                        <span className="text-green-600 font-bold text-sm">✓</span>
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-gray-900">Youth Leadership</h4>
                                        <p className="text-gray-600">Demonstrate ability to lead, teach, and mentor young people effectively</p>
                                    </div>
                                </div>
                                <div className="flex items-start">
                                    <div className="bg-green-100 rounded-full p-2 mr-4 mt-1">
                                        <span className="text-green-600 font-bold text-sm">✓</span>
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-gray-900">Practical Ministry</h4>
                                        <p className="text-gray-600">Engage in community service and practical ministry opportunities</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* About This Site Section */}
            <div className="py-20 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                            About This Digital Platform
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            This digital platform is designed to support Master Guide candidates and instructors throughout the certification journey.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        <div className="text-center">
                            <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                                <span className="text-2xl">📱</span>
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-4">Digital Tracking</h3>
                            <p className="text-gray-600">
                                Keep track of your progress through all Master Guide requirements with digital checklists and progress indicators.
                            </p>
                        </div>

                        <div className="text-center">
                            <div className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                                <span className="text-2xl">📖</span>
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-4">Study Resources</h3>
                            <p className="text-gray-600">
                                Access comprehensive study materials, Bible references, and educational content for each requirement area.
                            </p>
                        </div>

                        <div className="text-center">
                            <div className="bg-purple-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                                <span className="text-2xl">🎓</span>
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-4">Certification</h3>
                            <p className="text-gray-600">
                                Generate certificates and track completion status for instructors and conference administrators.
                            </p>
                        </div>
                    </div>

                    <div className="mt-16 text-center">
                        <div className="bg-blue-50 rounded-2xl p-8 md:p-12">
                            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">Ready to Begin?</h3>
                            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
                                Join thousands of Master Guide candidates and instructors using this platform to track progress, access resources, and achieve certification.
                            </p>
                            <button
                                onClick={onSignUp}
                                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-12 rounded-lg text-lg transition duration-200 transform hover:scale-105"
                            >
                                Create Your Account
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <footer className="bg-gradient-to-r from-gray-900 via-slate-900 to-gray-900 text-white py-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid md:grid-cols-4 gap-8 mb-12">
                        {/* Brand */}
                        <div className="md:col-span-2">
                            <div className="flex items-center space-x-3 mb-4">
                                <div className="w-12 h-12 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-full flex items-center justify-center">
                                    <span className="text-white font-bold text-xl">MG</span>
                                </div>
                                <div>
                                    <h3 className="text-2xl font-bold bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">
                                        Master Guide
                                    </h3>
                                    <p className="text-gray-400 text-sm">Pathfinder Leadership Platform</p>
                                </div>
                            </div>
                            <p className="text-gray-400 mb-6 leading-relaxed max-w-md">
                                Supporting spiritual leadership development in the Seventh-day Adventist Church.
                                Empowering youth leaders through faith, service, and practical skills.
                            </p>
                            <div className="flex space-x-4">
                                <div className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-indigo-600 transition duration-300 cursor-pointer">
                                    <span>📧</span>
                                </div>
                                <div className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-indigo-600 transition duration-300 cursor-pointer">
                                    <span>🌐</span>
                                </div>
                                <div className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-indigo-600 transition duration-300 cursor-pointer">
                                    <span>📱</span>
                                </div>
                            </div>
                        </div>

                        {/* Quick Links */}
                        <div>
                            <h4 className="text-lg font-semibold mb-4 text-white">Quick Links</h4>
                            <ul className="space-y-2">
                                <li><a href="#about" className="text-gray-400 hover:text-indigo-400 transition duration-300">About Master Guide</a></li>
                                <li><a href="#requirements" className="text-gray-400 hover:text-indigo-400 transition duration-300">Requirements</a></li>
                                <li><a href="#features" className="text-gray-400 hover:text-indigo-400 transition duration-300">Platform Features</a></li>
                                <li><a href="#support" className="text-gray-400 hover:text-indigo-400 transition duration-300">Support</a></li>
                            </ul>
                        </div>

                        {/* Resources */}
                        <div>
                            <h4 className="text-lg font-semibold mb-4 text-white">Resources</h4>
                            <ul className="space-y-2">
                                <li><a href="#" className="text-gray-400 hover:text-indigo-400 transition duration-300">Bible Study Guide</a></li>
                                <li><a href="#" className="text-gray-400 hover:text-indigo-400 transition duration-300">28 Beliefs</a></li>
                                <li><a href="#" className="text-gray-400 hover:text-indigo-400 transition duration-300">Honours Handbook</a></li>
                                <li><a href="#" className="text-gray-400 hover:text-indigo-400 transition duration-300">Leadership Training</a></li>
                            </ul>
                        </div>
                    </div>

                    {/* Bottom Bar */}
                    <div className="border-t border-gray-800 pt-8">
                        <div className="flex flex-col md:flex-row justify-between items-center">
                            <div className="text-gray-400 text-sm mb-4 md:mb-0">
                                © 2025 Master Guide Digital Platform. Part of the Pathfinder Ministry.
                            </div>
                            <div className="flex items-center space-x-6 text-sm text-gray-400">
                                <a href="#" className="hover:text-indigo-400 transition duration-300">Privacy Policy</a>
                                <a href="#" className="hover:text-indigo-400 transition duration-300">Terms of Service</a>
                                <a href="#" className="hover:text-indigo-400 transition duration-300">Contact</a>
                            </div>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}
